import { FC, useState } from 'react';
import { Col, Form, Input, Row, Select } from 'antd';
import rule from '@/libs/antd/rules';
import { useForm } from '@/libs/antd/form';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { getNAPUser, NAPUser } from '@/libs/fleet-api/services/nap';
import useQuerySelect from '@/hooks/useQuerySelect';

type ProposerData = {
	user: NAPUser;
	index: number;
};

type ModalData =
	| { type: 'new' }
	| {
			type: 'edit';
			data: ProposerData;
	  };

export const useProposerModal = (
	onSubmit: (type: ModalData['type'], data: ProposerData) => void,
) => {
	const [modalData, setModalData] = useState<ModalData>();

	return {
		data: modalData,
		isOpen: Boolean(modalData),
		open: (data: ModalData) => setModalData(data),
		close: () => setModalData(undefined),
		onSubmit,
	};
};

const ProposerModal: FC<ReturnType<typeof useProposerModal>> = (props) => {
	const { form, value, fieldName } = useForm<ProposerData>();

	const selectUsers = useQuerySelect({
		queryKey: ['/naps/get-users'],
		queryFn: async (pageNumber, keys) => {
			const res = await getNAPUser({
				pagination: {
					pageSize: 10,
					pageNumber,
				},
				search: {
					by: 'name',
					value: keys[1],
				},
			});

			return res.data.items.map((data) => {
				return {
					label: data.name,
					value: data.id,
					data,
				};
			});
		},
		infinite: true,
	});

	return (
		<FleetModal
			open={props.isOpen}
			width={940}
			closable={false}
			buttonPosition="end"
			okText="SAVE"
			cancelText="BACK"
			onCancel={() => props.close()}
			onOk={form.submit}
			afterOpenChange={(isOpen) => {
				if (!isOpen) {
					form.resetFields();
				} else if (props.data?.type === 'edit') {
					form.setFieldsValue(props.data.data);
				}
			}}
		>
			<FleetTitle>
				{props.data?.type === 'edit' ? 'Edit' : 'Add'} Pengusul
			</FleetTitle>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={(values) => {
					props.close();
					props.onSubmit(props.data!.type, values);
				}}
			>
				<Form.Item
					name={fieldName('index')}
					noStyle
				/>
				<Row
					gutter={40}
					style={{ marginBlock: 20 }}
				>
					<Col span={12}>
						<Form.Item
							label="Nama"
							name={fieldName('user')}
							rules={[rule.required()]}
							getValueFromEvent={(_, option) => option?.data}
							getValueProps={(value) => {
								if (!value) return {};
								return {
									value: {
										value: value.id,
										label: value.name,
										data: value,
									},
								};
							}}
						>
							<Select {...selectUsers} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Jabatan">
							<Input
								value={value?.user?.role}
								disabled
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>
		</FleetModal>
	);
};

export default ProposerModal;
